/* Modular Plus configurator
 * - reads the JSON data island rendered by snippets/mp-configurator.liquid
 * - package + option/toggle selection, live total, localStorage save/restore
 * - adds model (parent) + selected add-ons (children) to cart via /cart/add.js,
 *   linking children to the parent line with properties (_parent_id, _bundle).
 *
 * UI mirrors the static HTML mockup, but is fully data-driven:
 *   - icons / "none" (⊘) state / price-notes are rendered server-side from metafields
 *   - group dependencies come from data.groups[].dependsOn / dependsKind
 *   - dependent option filtering uses each swatch's data-mp-parent attribute
 */
(function () {
  'use strict';

  var euro = new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 });
  function money(cents) { return euro.format((cents || 0) / 100); }

  function initAll() {
    document.querySelectorAll('[data-mp-configurator]').forEach(initOne);
  }

  function initOne(root) {
    if (root.__mpInit) return;
    root.__mpInit = true;

    var dataEl = root.querySelector('[data-mp-data]');
    if (!dataEl) return;
    var data;
    var raw = dataEl.textContent;
    try {
      data = JSON.parse(raw);
    } catch (e) {
      // Tolerate stray commas from server-side templating (trailing ",}" / ",]" or leading "{," / "[,").
      var cleaned = raw
        .replace(/,(\s*[}\]])/g, '$1')
        .replace(/([{[])(\s*),/g, '$1$2');
      try {
        data = JSON.parse(cleaned);
      } catch (e2) {
        console.error('MP config parse error', e2, '\n--- raw JSON ---\n', raw);
        return;
      }
    }

    var modelHandle = root.getAttribute('data-model-handle');
    var saveEnabled = root.getAttribute('data-save') === 'true';
    var quoteUrl = root.getAttribute('data-quote-url') || '/cart';
    var storeKey = 'mp-config-v2-' + modelHandle; // v2: paid extras reset on package change

    // ---- DOM refs ----
    var packageCards = Array.prototype.slice.call(root.querySelectorAll('[data-mp-package]'));
    var groupEls = Array.prototype.slice.call(root.querySelectorAll('[data-mp-group]'));
    var totalEl = root.querySelector('[data-mp-total]');
    var pkgSelectedEl = root.querySelector('[data-mp-package-selected]');
    var noticeEl = root.querySelector('[data-mp-notice]');
    var addBtn = root.querySelector('[data-mp-add]');
    var quoteBtn = root.querySelector('[data-mp-quote]');

    function cssEsc(v) { return (window.CSS && CSS.escape) ? CSS.escape(v) : String(v).replace(/"/g, '\\"'); }
    function groupEl(key) { return root.querySelector('[data-mp-group="' + cssEsc(key) + '"]'); }

    // Fallback icon when an option has no server-rendered `icon` field — mirrors the
    // mockup's per-group icons so tiles aren't blank if the metafield is unset.
    function fallbackIcon(key, label, isNone) {
      if (isNone) return '⊘';
      var k = (key || '').toLowerCase();
      var l = (label || '').toLowerCase();
      var premium = /premium|miele|autopilot/.test(l);
      if (k.indexOf('bath') > -1 || k.indexOf('bad') > -1) return '🚿';
      if (k.indexOf('kitchen') > -1 || k.indexOf('kuche') > -1 || k.indexOf('küche') > -1) return premium ? '★' : '🍳';
      if (k.indexOf('laundry') > -1 || k.indexOf('wasch') > -1) return premium ? '★' : '🌀';
      if (k.indexOf('smart') > -1) return premium ? '★' : '◐';
      return '';
    }

    // group meta by key (from data.groups), for productHandle / optionName / dependency lookups
    var groupMeta = {};
    data.groups.forEach(function (g) { groupMeta[g.key] = g; });

    // ---- dependency rules (data-driven) ----
    // a group is visible only when its dependency is satisfied; the dependency
    // kind tells us whether to read the parent's swatch selection or toggle state.
    function groupDep(key) {
      var m = groupMeta[key];
      return (m && m.dependsOn) ? { on: m.dependsOn, kind: m.dependsKind || 'swatch' } : null;
    }
    function groupVisible(key) {
      var dep = groupDep(key);
      if (!dep) return true;
      return dep.kind === 'toggle' ? !!state.toggle[dep.on] : !!state.sel[dep.on];
    }
    // value selected in the parent group a dependent group hangs off of
    function parentValue(key) {
      var dep = groupDep(key);
      if (!dep || dep.kind === 'toggle') return null;
      return state.sel[dep.on] || null;
    }
    // does this swatch belong to the currently chosen parent? (data-mp-parent filter)
    function optionMatchesParent(swatchEl, pVal) {
      var pp = swatchEl.getAttribute('data-mp-parent');
      return !pp || !pVal || pp === pVal;
    }

    // ---- state ----
    var state = {
      pkgKey: data.model.packages[0] ? data.model.packages[0].key : null,
      sel: {},     // swatch group key -> selected variant_value (null = "none")
      toggle: {},  // toggle group key -> bool
      auto: {},    // group key -> true when *we* picked it for an included package, not the customer
    };

    // ---- helpers ----
    function selectedPackage() {
      return data.model.packages.filter(function (p) { return p.key === state.pkgKey; })[0] || data.model.packages[0];
    }
    function includesForPackage() {
      var inc = (data.model.packageIncludes || {})[state.pkgKey];
      return Array.isArray(inc) ? inc : [];
    }
    // A package can contain a group in two ways, one field each (per product):
    //   model.package_includes — COMPLETELY: every option of the group is free
    //   model.package_tiers    — ONE TIER:   that option is free, dearer ones cost the difference
    //                            (PURE: silver kitchen + AEG appliances; Miele costs + 9.000 €)
    // A tier wins when a group is listed in both.
    function r_included(key) {
      return includesForPackage().indexOf(key) !== -1 || tierValue(key) !== null;
    }
    function tiersForPackage() {
      var t = (data.model.packageTiers || {})[state.pkgKey];
      return (t && typeof t === 'object') ? t : {};
    }
    function tierValue(key) {
      var t = tiersForPackage();
      return Object.prototype.hasOwnProperty.call(t, key) ? t[key] : null;
    }
    // first real (non-"none") option value in a group — used to resolve a variant
    function firstRealOptionValue(gEl) {
      var s = gEl.querySelector('[data-mp-option]:not([data-mp-none])');
      return s ? s.getAttribute('data-mp-option') : null;
    }
    function optionLabel(gEl, value) {
      var s = gEl.querySelector('[data-mp-option="' + cssEsc(value) + '"]');
      return s ? s.getAttribute('data-label') : value;
    }
    // label of a group's "none" option, if any (shown when nothing is selected)
    function noneLabel(gEl) {
      var s = gEl.querySelector('[data-mp-none]');
      return s ? s.getAttribute('data-label') : '';
    }

    // resolve a variant for a product handle given {optionName: value}
    function resolveVariant(handle, selValues) {
      var list = (data.addons || {})[handle] || [];
      for (var i = 0; i < list.length; i++) {
        var v = list[i], ok = true;
        for (var name in selValues) {
          if (selValues[name] == null) continue;
          if (v.opts[name] !== selValues[name]) { ok = false; break; }
        }
        if (ok) return v;
      }
      return null;
    }

    // group the config groups by their product handle
    function groupsByProduct() {
      var map = {};
      data.groups.forEach(function (g) {
        if (!g.productHandle) return; // finish groups have no product
        (map[g.productHandle] = map[g.productHandle] || []).push(g);
      });
      return map;
    }

    // ---- core compute ----
    function compute() {
      var pkg = selectedPackage();
      var includes = includesForPackage();
      var total = pkg ? pkg.price : 0;
      var children = [];
      var parentProps = { 'Modell': data.model.title, 'Paket': pkg ? pkg.name : '' };

      var byProduct = groupsByProduct();
      Object.keys(byProduct).forEach(function (handle) {
        var groups = byProduct[handle];
        var included = groups.length > 0 && groups.every(function (g) { return r_included(g.key); });

        // gather selections for this product
        var selValues = {};
        var active = false;
        groups.forEach(function (g) {
          if (g.hasOptions) {
            var val = state.sel[g.key];
            // blank optionName = finish-swatch add-on (e.g. interior wood): any non-none choice
            // activates the single model variant; the colour is recorded as a property below.
            if (val) { if (g.optionName) selValues[g.optionName] = val; active = true; }
          } else if (state.toggle[g.key]) {
            active = true;
          }
        });

        if (included) {
          // the covered tier is baked into the package price; an upgrade costs the difference
          var chosenSel = {}, tierSel = {}, hasTier = false;
          groups.forEach(function (g) {
            if (!g.hasOptions) return;
            var el = groupEl(g.key);
            var val = state.sel[g.key] || (el ? firstRealOptionValue(el) : null);
            if (val && el) parentProps[g.name] = optionLabel(el, val);
            if (!g.optionName || !val) return;
            chosenSel[g.optionName] = val;
            var tv = tierValue(g.key);
            if (tv) { tierSel[g.optionName] = tv; hasTier = true; }
            else tierSel[g.optionName] = val; // completely included: any pick is free
          });
          if (hasTier) {
            var baseVar = resolveVariant(handle, tierSel);
            var chosenVar = resolveVariant(handle, chosenSel);
            if (baseVar && chosenVar && chosenVar.price > baseVar.price) {
              total += chosenVar.price - baseVar.price;
            }
          }
          return;
        }

        if (!active) return;

        // fill missing option dimensions with their first real option so a variant resolves
        groups.forEach(function (g) {
          if (g.hasOptions && g.optionName && !selValues[g.optionName]) {
            var el = groupEl(g.key);
            if (el) selValues[g.optionName] = firstRealOptionValue(el);
          }
        });

        var variant = resolveVariant(handle, selValues);
        if (!variant) return;
        total += variant.price;

        // child line properties (visible + linking)
        var props = { 'Konfiguration': data.model.title + ' · ' + (pkg ? pkg.name : '') };
        groups.forEach(function (g) {
          if (!g.hasOptions) return;
          var el = groupEl(g.key);
          var val = g.optionName ? selValues[g.optionName] : state.sel[g.key];
          if (val && el) props[g.name] = optionLabel(el, val);
        });
        children.push({ id: variant.id, properties: props });
      });

      // finish groups (no product): free choice recorded as a property on the parent line
      data.groups.forEach(function (g) {
        if (g.productHandle || !g.hasOptions) return;
        var val = state.sel[g.key];
        if (!val) return;
        var el = groupEl(g.key);
        if (el) parentProps[g.name] = optionLabel(el, val);
      });

      return { pkg: pkg, total: total, children: children, parentProps: parentProps, includes: includes };
    }

    // ---- normalize dependent groups before pricing/rendering ----
    // clear hidden selections, drop selections that don't belong to the chosen
    // parent, and give visible dependent *finish* groups a sensible default.
    function normalize() {
      // Selections we made for an included group are dropped when the package no longer
      // includes it — otherwise DIY inherits (and charges for) e.g. the Badezimmerboden that
      // PURE had preselected. A choice the customer made themselves is kept.
      Object.keys(state.auto).forEach(function (key) {
        if (!state.auto[key] || r_included(key)) return;
        state.sel[key] = null;
        state.auto[key] = false;
      });
      // included groups next: a group that hangs off one of them (e.g. Badezimmerboden under
      // Badezimmer) can only become visible once its parent holds a real selection
      normalizeIncluded();
      data.groups.forEach(function (g) {
        if (!g.dependsOn) return;
        var key = g.key;
        var el = groupEl(key);
        if (!el) return;
        if (!groupVisible(key)) { state.sel[key] = null; return; }

        var pVal = parentValue(key);
        // drop a selection that belongs to a different parent
        var cur = state.sel[key];
        if (cur) {
          var curOpt = el.querySelector('[data-mp-option="' + cssEsc(cur) + '"]');
          if (curOpt && !optionMatchesParent(curOpt, pVal)) { cur = null; state.sel[key] = null; }
        }
        // visible dependent finish group with no selection -> pick first valid option
        if (!cur && !g.productHandle) {
          var firstValid = null;
          el.querySelectorAll('[data-mp-option]').forEach(function (s) {
            if (firstValid || s.hasAttribute('data-mp-none')) return;
            if (optionMatchesParent(s, pVal)) firstValid = s.getAttribute('data-mp-option');
          });
          if (firstValid) state.sel[key] = firstValid;
        }
      });
      normalizeIncluded();
    }

    // Groups the package already covers (e.g. Badezimmerboden in Pure/Select/Ultimate) are
    // free of charge but still the customer's choice — keep a real option selected in them,
    // because "none" is not a valid answer for something that is part of the package.
    function normalizeIncluded() {
      data.groups.forEach(function (g) {
        if (!g.hasOptions || !r_included(g.key)) return;
        var el = groupEl(g.key);
        if (!el || !groupVisible(g.key)) return;
        var pVal = parentValue(g.key);
        var cur = state.sel[g.key];
        if (cur) {
          var curOpt = el.querySelector('[data-mp-option="' + cssEsc(cur) + '"]');
          // keep the current pick unless it is "none" or belongs to another parent
          if (curOpt && !curOpt.hasAttribute('data-mp-none') && optionMatchesParent(curOpt, pVal)) return;
        }
        var firstValid = null;
        var tv = tierValue(g.key); // the tier the package covers wins as the default
        if (tv && el.querySelector('[data-mp-option="' + cssEsc(tv) + '"]')) firstValid = tv;
        el.querySelectorAll('[data-mp-option]').forEach(function (s) {
          if (firstValid || s.hasAttribute('data-mp-none')) return;
          if (optionMatchesParent(s, pVal)) firstValid = s.getAttribute('data-mp-option');
        });
        state.sel[g.key] = firstValid;
        state.auto[g.key] = true;
      });
    }

    // ---- render ----
    function render() {
      normalize();
      var r = compute();

      // packages
      packageCards.forEach(function (c) {
        c.classList.toggle('selected', c.getAttribute('data-mp-package') === state.pkgKey);
      });
      if (pkgSelectedEl && r.pkg) pkgSelectedEl.textContent = r.pkg.name;

      // savings notice (per package)
      if (noticeEl) {
        var sv = (data.model.savings || {})[state.pkgKey] || 0;
        var base = noticeEl.getAttribute('data-base') || '';
        noticeEl.innerHTML = sv > 0
          ? 'Mit diesem Paket sparst du <strong>' + money(sv * 100) + '</strong> im Vergleich zur Einzelauswahl.'
          : base;
      }

      // groups
      groupEls.forEach(function (gEl) {
        var key = gEl.getAttribute('data-mp-group');
        var meta = groupMeta[key];
        var isIncluded = meta && r_included(key);
        var isSub = gEl.classList.contains('option-sub-section');
        gEl.classList.toggle('mp-included', !!isIncluded);

        // dependency visibility
        gEl.style.display = groupVisible(key) ? '' : 'none';
        // dependent groups: only show options belonging to the chosen parent value
        var pVal = parentValue(key);
        if (pVal != null || groupDep(key)) {
          gEl.querySelectorAll('[data-mp-option]').forEach(function (s) {
            s.style.display = optionMatchesParent(s, pVal) ? '' : 'none';
          });
        }
        // included = part of the package price, so switching the group off makes no sense
        gEl.querySelectorAll('[data-mp-none]').forEach(function (s) {
          if (isIncluded) s.style.display = 'none';
          else if (!groupDep(key)) s.style.display = '';
        });

        // swatches
        var swatches = gEl.querySelectorAll('[data-mp-option]');
        if (swatches.length) {
          var sel = state.sel[key];
          var gHandle = gEl.getAttribute('data-product-handle');
          var gOptionName = gEl.getAttribute('data-option-name');
          swatches.forEach(function (s) {
            var val = s.getAttribute('data-mp-option');
            var isNone = s.hasAttribute('data-mp-none');
            // "none" swatch is selected when the group has no active selection (and isn't package-included)
            var isSelected = isNone ? (!sel && !isIncluded) : (val === sel);
            s.classList.toggle('selected', isSelected);

            // fill the icon tile if the metafield didn't provide one — but never on a
            // material/finish image swatch (e.g. SPC Bad marble textures); icons belong
            // only on the colour-tile fixture groups (bathroom, kitchen, laundry, smart).
            var iconEl = s.querySelector('[data-mp-swatch-icon]');
            var imgDiv = iconEl ? iconEl.parentElement : null;
            var hasImage = !!(imgDiv && imgDiv.style && imgDiv.style.backgroundImage && imgDiv.style.backgroundImage !== 'none' && imgDiv.style.backgroundImage !== '');
            if (iconEl && !iconEl.textContent.trim() && !hasImage) {
              var ic = fallbackIcon(key, s.getAttribute('data-label') || val, isNone);
              if (ic) iconEl.textContent = ic;
            }

            // price label — leave server-rendered static notes ("—", "+ 23.000 €") alone
            var priceEl = s.querySelector('[data-mp-swatch-price]');
            if (priceEl) {
              // remember the server-rendered note once, so leaving an including package restores it
              if (priceEl.getAttribute('data-mp-price-base') === null) {
                priceEl.setAttribute('data-mp-price-base', priceEl.textContent);
              }
              if (isIncluded) {
                var sur = includedSurcharge(gEl, key, gHandle, gOptionName, val, isNone);
                priceEl.textContent = sur > 0 ? '+ ' + money(sur) : 'inkl.';
              } else if (priceEl.hasAttribute('data-mp-static')) {
                priceEl.textContent = priceEl.getAttribute('data-mp-price-base');
              } else if (gHandle) {
                var price = swatchPrice(gEl, key, gHandle, gOptionName, val, isNone);
                if (price != null && isSubDimension(key)) {
                  var basePrice = swatchPrice(gEl, key, gHandle, gOptionName, firstRealOptionValue(gEl), false);
                  var diff = basePrice != null ? price - basePrice : null;
                  priceEl.textContent = diff == null ? '' : (diff > 0 ? '+ ' + money(diff) : 'inkl.');
                } else {
                  priceEl.textContent = (price != null ? '+ ' + money(price) : '');
                }
              }
            }
          });
          var lbl = gEl.querySelector('[data-mp-group-selected]');
          if (lbl) {
            var prefix = isSub ? '— ' : '';
            var selSur = isIncluded && sel ? includedSurcharge(gEl, key, gHandle, gOptionName, sel, false) : null;
            var incLabel = sel
              ? optionLabel(gEl, sel) + (selSur > 0 ? ' · + ' + money(selSur) : ' · inklusive')
              : 'inklusive';
            lbl.textContent = isIncluded
              ? (isSub ? prefix + incLabel : ' · ' + incLabel)
              : (sel ? prefix + optionLabel(gEl, sel) : (isSub ? prefix + noneLabel(gEl) : (noneLabel(gEl) ? ' · ' + noneLabel(gEl) : '')));
          }
        }

        // toggle (the toggle element may BE the group element, e.g. an addon row)
        var toggle = gEl.matches('[data-mp-toggle]') ? gEl : gEl.querySelector('[data-mp-toggle]');
        if (toggle) {
          var handle = gEl.getAttribute('data-product-handle');
          var v = resolveVariant(handle, {}); // model-filtered single variant
          var tPriceEl = gEl.querySelector('[data-mp-toggle-price]');
          if (isIncluded) {
            toggle.classList.add('checked', 'included');
            if (tPriceEl) tPriceEl.textContent = 'inkl.';
            ensureIncludedTag(gEl);
          } else {
            toggle.classList.toggle('checked', !!state.toggle[key]);
            toggle.classList.remove('included');
            removeIncludedTag(gEl);
            if (tPriceEl) tPriceEl.textContent = v ? '+ ' + money(v.price) : '';
          }
        }
      });

      if (popAnchor && popAnchor.offsetParent === null) closePop();
      if (totalEl) totalEl.textContent = money(r.total);
      save();
    }

    // resolve the add-on price shown on a single swatch, combining the other
    // option dimensions of the same product with their current selections
    // A group that hangs off another group of the SAME product is an extra dimension of it
    // (Küchengeräte under Küche). Its tiles show only their own surcharge (Miele + 9.000 €),
    // and the parent's tiles price the product with that dimension on its base option — so
    // the two rows add up to the total instead of both showing the full kitchen price.
    function isSubDimension(key) {
      var m = groupMeta[key];
      if (!m || !m.dependsOn || !m.productHandle) return false;
      var parent = groupMeta[m.dependsOn];
      return !!(parent && parent.productHandle === m.productHandle);
    }

    function swatchPrice(gEl, key, gHandle, gOptionName, val, isNone) {
      if (isNone) return null;
      var sv = {};
      if (gOptionName) sv[gOptionName] = val;
      data.groups.forEach(function (g2) {
        if (g2.productHandle !== gHandle || g2.key === key || !g2.optionName) return;
        var el2 = groupEl(g2.key);
        var v2 = isSubDimension(g2.key) ? null : state.sel[g2.key]; // sub-dimension stays on its base
        if (!v2) v2 = el2 ? firstRealOptionValue(el2) : null;
        if (v2) sv[g2.optionName] = v2;
      });
      var vr = resolveVariant(gHandle, sv);
      return vr ? vr.price : null;
    }

    // surcharge shown on one tile of an included group: difference to the covered tier
    // (null = no tier configured, so the whole group stays free)
    function includedSurcharge(gEl, key, gHandle, gOptionName, val, isNone) {
      if (!gHandle || isNone || !gOptionName) return null;
      var tv = tierValue(key);
      if (!tv) return 0; // completely included: every option is free
      var chosen = {}, base = {};
      chosen[gOptionName] = val;
      base[gOptionName] = tv;
      // The other dimensions of the same product stay on the tier the package covers, so a
      // tile always shows its own surcharge ("Schwarz + 1.000 €") no matter what else is
      // picked. The total is computed from the real combination in compute().
      data.groups.forEach(function (g2) {
        if (g2.productHandle !== gHandle || g2.key === key || !g2.optionName) return;
        var t2 = tierValue(g2.key);
        if (!t2) {
          var el2 = groupEl(g2.key);
          t2 = state.sel[g2.key] || (el2 ? firstRealOptionValue(el2) : null);
        }
        if (t2) { chosen[g2.optionName] = t2; base[g2.optionName] = t2; }
      });
      var cv = resolveVariant(gHandle, chosen), bv = resolveVariant(gHandle, base);
      if (!cv || !bv) return null;
      return cv.price > bv.price ? cv.price - bv.price : 0;
    }

    function ensureIncludedTag(gEl) {
      var nameEl = gEl.querySelector('.addon-name');
      if (!nameEl || gEl.querySelector('.addon-tag')) return;
      var tag = document.createElement('span');
      tag.className = 'addon-tag';
      tag.setAttribute('data-mp-included-tag', '');
      tag.textContent = 'Inkl.';
      nameEl.insertAdjacentElement('afterend', tag);
    }
    function removeIncludedTag(gEl) {
      var tag = gEl.querySelector('[data-mp-included-tag]');
      if (tag) tag.remove();
    }

    // ---- swatch preview popover ----
    // Tapping a tile also shows it large in a tooltip, placed above / below / right / left
    // depending on where there is room. It lives on <body> so nothing can clip it, and it
    // never takes pointer events, so it can't get in the way of the next click.
    var pop = null, popAnchor = null, popTimer = null;
    // touch devices have no hover, so there a tap opens the preview instead
    var canHover = !window.matchMedia || window.matchMedia('(hover: hover)').matches;

    function schedulePop(sw) {
      clearTimeout(popTimer);
      popTimer = setTimeout(function () { openPop(sw); }, 120);
    }
    function cancelPop() {
      clearTimeout(popTimer);
      closePop();
    }

    function popEl() {
      if (pop) return pop;
      pop = document.createElement('div');
      pop.className = 'mp-swatch-pop';
      pop.setAttribute('role', 'tooltip');
      pop.innerHTML =
        '<div class="mp-swatch-pop-media" data-pop-media></div>' +
        '<div class="mp-swatch-pop-label" data-pop-label></div>' +
        '<div class="mp-swatch-pop-price" data-pop-price></div>' +
        '<span class="mp-swatch-pop-arrow"></span>';
      document.body.appendChild(pop);
      return pop;
    }

    function closePop() {
      clearTimeout(popTimer);
      if (pop) pop.classList.remove('open');
      popAnchor = null;
    }

    function openPop(sw) {
      var img = sw.getAttribute('data-mp-preview-img');
      var col = sw.getAttribute('data-mp-preview-color');
      // the "none" tile has nothing worth enlarging
      if (sw.hasAttribute('data-mp-none') || (!img && !col)) { closePop(); return; }

      var p = popEl();
      var media = p.querySelector('[data-pop-media]');
      if (img) {
        media.style.background = '';
        media.style.backgroundImage = 'url("' + img + '")';
      } else {
        media.style.backgroundImage = '';
        media.style.background = 'linear-gradient(135deg, ' + col + ', ' + col + 'bb)';
      }
      p.querySelector('[data-pop-label]').textContent = sw.getAttribute('data-label') || '';
      var priceEl = sw.querySelector('[data-mp-swatch-price]');
      p.querySelector('[data-pop-price]').textContent = priceEl ? priceEl.textContent.trim() : '';

      popAnchor = sw;
      p.classList.add('open');
      placePop();
    }

    // pick the side that has room, then keep the whole box inside the viewport
    function placePop() {
      if (!pop || !popAnchor || !pop.classList.contains('open')) return;
      var r = popAnchor.getBoundingClientRect();
      var pw = pop.offsetWidth, ph = pop.offsetHeight;
      var vw = document.documentElement.clientWidth, vh = document.documentElement.clientHeight;
      var gap = 10, edge = 8;

      var place = r.top >= ph + gap ? 'top'
        : (vh - r.bottom) >= ph + gap ? 'bottom'
        : (vw - r.right) >= pw + gap ? 'right'
        : r.left >= pw + gap ? 'left'
        : ((vh - r.bottom) >= r.top ? 'bottom' : 'top');

      var left, top;
      if (place === 'top' || place === 'bottom') {
        left = r.left + r.width / 2 - pw / 2;
        top = place === 'top' ? r.top - ph - gap : r.bottom + gap;
      } else {
        left = place === 'right' ? r.right + gap : r.left - pw - gap;
        top = r.top + r.height / 2 - ph / 2;
      }
      left = Math.min(Math.max(edge, left), Math.max(edge, vw - pw - edge));
      top = Math.min(Math.max(edge, top), Math.max(edge, vh - ph - edge));

      pop.style.left = Math.round(left) + 'px';
      pop.style.top = Math.round(top) + 'px';
      pop.setAttribute('data-place', place);
      // arrow points back at the tile, clamped to the popover's own edges
      pop.style.setProperty('--arrow-x', Math.min(Math.max(10, r.left + r.width / 2 - left - 6), pw - 22) + 'px');
      pop.style.setProperty('--arrow-y', Math.min(Math.max(10, r.top + r.height / 2 - top - 6), ph - 22) + 'px');
    }

    window.addEventListener('scroll', placePop, { passive: true });
    window.addEventListener('resize', placePop);
    document.addEventListener('click', function (e) {
      if (!popAnchor) return;
      if (e.target.closest && e.target.closest('[data-mp-option]')) return; // the tile handles itself
      closePop();
    });

    // ---- product gallery per package ----
    // Package cards ARE product variants (option1 = package key), so we reuse the theme's
    // own variant plumbing instead of building a second gallery: <product-information>
    // re-fetches the section for that variant and swaps [data-product-media-wrapper] when
    // the section setting "show only variant media" is on, and <product-media> slides to
    // the variant's featured image when it isn't. Requires images attached to the package
    // variants in the admin.
    var productUrl = root.getAttribute('data-product-url') || window.location.pathname;
    var shownVariantId = String(root.getAttribute('data-current-variant') || '');

    function syncGallery() {
      var pkg = selectedPackage();
      if (!pkg || !pkg.variantId || !window.eventBus) return;
      var id = String(pkg.variantId);
      if (id === shownVariantId) return; // gallery already shows this package
      shownVariantId = id;
      window.eventBus.emit('variant:change', {
        sectionId: root.getAttribute('data-section-id'),
        variant: {
          id: pkg.variantId,
          available: pkg.available !== false,
          featured_media: pkg.featuredMediaId ? { id: pkg.featuredMediaId } : null,
        },
        fetchURL: productUrl + '?variant=' + id,
        productURL: productUrl,
        isCombinedListing: false,
      });
    }

    // Paid add-ons were chosen for the package the customer was looking at, so switching
    // package starts from that package's price again — picking DIY shows the DIY price, not
    // the extras of the package before it. Free finish choices (floors, walls, colours) stay.
    function resetPaidExtras() {
      data.groups.forEach(function (g) {
        if (!g.productHandle) return;
        state.sel[g.key] = null;
        state.toggle[g.key] = false;
        state.auto[g.key] = false;
      });
    }

    // ---- events ----
    packageCards.forEach(function (c) {
      c.addEventListener('click', function () {
        if (c.getAttribute('data-mp-package') === state.pkgKey) return;
        state.pkgKey = c.getAttribute('data-mp-package');
        resetPaidExtras();
        render();
        syncGallery();
      });
    });

    groupEls.forEach(function (gEl) {
      var key = gEl.getAttribute('data-mp-group');
      // swatch click (single-select; "none" or clicking the selected swatch clears it)
      gEl.querySelectorAll('[data-mp-option]').forEach(function (s) {
        if (canHover) {
          s.addEventListener('mouseenter', function () { schedulePop(s); });
          s.addEventListener('mouseleave', cancelPop);
        }
        s.addEventListener('click', function () {
          // included groups stay selectable — the choice is free, only "none" is gone
          if (s.hasAttribute('data-mp-none')) {
            state.sel[key] = null;
          } else {
            var val = s.getAttribute('data-mp-option');
            // clicking the active tile clears it — except in an included group, which
            // always has to hold one option
            state.sel[key] = (state.sel[key] === val && !r_included(key)) ? null : val;
          }
          state.auto[key] = false; // from now on it is the customer's choice
          render();
          if (!canHover) { if (popAnchor === s) closePop(); else openPop(s); } // touch: tap previews
        });
      });
      // toggle click (element may be the group element itself)
      var toggle = gEl.matches('[data-mp-toggle]') ? gEl : gEl.querySelector('[data-mp-toggle]');
      if (toggle) {
        toggle.addEventListener('click', function () {
          if (r_included(key)) return;
          state.toggle[key] = !state.toggle[key];
          render();
        });
      }
    });

    // ---- cart ----
    function buildItems() {
      var r = compute();
      if (!r.pkg || !r.pkg.variantId) return null;
      var bundle = 'mp-' + Date.now() + '-' + Math.floor(Math.random() * 1e6);
      var items = [];
      // The parent (package) line carries the bundle id; each child references that same id via
      // _parent_id, so children link to the parent bundle by id (the parent is the line with no _parent_id).
      var parentProps = Object.assign({}, r.parentProps, { _bundle: bundle });
      items.push({ id: r.pkg.variantId, quantity: 1, properties: parentProps });
      r.children.forEach(function (ch) {
        var props = Object.assign({}, ch.properties); //, { _bundle: bundle, _parent_id: bundle }
        items.push({ id: ch.id, quantity: 1, properties: props, parent_id: String(r.pkg.variantId) });
      });
      return items;
    }

    // cart style ('drawer' or 'page'), read the same way the theme's product form does
    function cartType() {
      var pc = document.getElementById('PageContainer');
      return (pc && pc.dataset && pc.dataset.cartType) || 'page';
    }
    var sectionId = root.getAttribute('data-section-id');

    // refresh the header cart-count bubble after an add
    function refreshCartCount() {
      if (!(window.wetheme && window.wetheme.updateCartCount)) return;
      fetch('/cart.js', { headers: { Accept: 'application/json' } })
        .then(function (r) { return r.json(); })
        .then(function (c) { window.wetheme.updateCartCount(c); })
        .catch(function () {});
    }

    // opts: { thenUrl, openDrawer }
    // When the cart is a drawer and openDrawer is set, we use Shopify's Section Rendering
    // API (sections: 'cart-drawer') and the theme's eventBus to update + open the drawer,
    // exactly like component-product-form.js — instead of navigating away.
    function addToCart(opts) {
      opts = opts || {};
      var items = buildItems();
      if (!items) { alert('Bitte ein Paket wählen.'); return; }
      var useDrawer = opts.openDrawer && cartType() === 'drawer' && window.eventBus;

      if (addBtn) { addBtn.disabled = true; addBtn.classList.add('is-loading'); }
      if (quoteBtn) quoteBtn.disabled = true;

      var payload = { items: items };
      if (useDrawer) payload.sections = 'cart-drawer'; // ask Shopify to render the drawer section

      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify(payload),
      })
        .then(function (res) { if (!res.ok) return res.json().then(function (e) { throw e; }); return res.json(); })
        .then(function (resJson) {
          document.dispatchEvent(new CustomEvent('mp:added', { detail: { items: items } }));
          if (useDrawer) {
            // resJson.sections['cart-drawer'] holds the freshly rendered drawer HTML
            window.eventBus.emit('update:cart:drawer', resJson);
            window.eventBus.emit('cart:added', { sectionId: sectionId });
            window.eventBus.emit('open:cart:drawer', { scrollToTop: true });
            refreshCartCount();
          } else {
            window.location.href = opts.thenUrl || '/cart';
          }
        })
        .catch(function (err) {
          console.error('MP add to cart failed', err);
          alert('Hinzufügen fehlgeschlagen: ' + (err && err.description ? err.description : 'Bitte erneut versuchen.'));
        })
        .finally(function () {
          if (addBtn) { addBtn.disabled = false; addBtn.classList.remove('is-loading'); }
          if (quoteBtn) quoteBtn.disabled = false;
        });
    }

    // ---- two popups (mockup style): Offer (config + contact) and Call (consultation). ----
    // Both submit via Shopify's contact form over AJAX (no page reload).
    var offerBtn = root.querySelector('[data-mp-offer]');
    var callBtn = root.querySelector('[data-mp-call]');
    var modals = Array.prototype.slice.call(document.querySelectorAll('[data-mp-modal]'));
    var offerModal = null, callModal = null;
    modals.forEach(function (m) {
      // move to <body> so the popup escapes the section's stacking context (sits above the header)
      if (m.parentNode !== document.body) document.body.appendChild(m);
      var kind = m.getAttribute('data-mp-modal');
      if (kind === 'offer') offerModal = m;
      if (kind === 'call') callModal = m;
    });

    function escapeHtml(str) {
      return String(str == null ? '' : str).replace(/[&<>"]/g, function (c) {
        return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c];
      });
    }
    // rows shown at the top of the Offer modal (package, each choice, total)
    function offerSummaryHTML() {
      var r = compute();
      var rows = '';
      rows += '<div class="row"><span>Modell</span><span>' + escapeHtml(data.model.title) + '</span></div>';
      rows += '<div class="row"><span>Paket</span><span>' + escapeHtml(r.pkg ? r.pkg.name : '—') + '</span></div>';
      data.groups.forEach(function (g) {
        var el = groupEl(g.key);
        if (!el) return;
        if (g.hasOptions) {
          var val = state.sel[g.key];
          if (val) rows += '<div class="row"><span>' + escapeHtml(g.name) + '</span><span>' + escapeHtml(optionLabel(el, val)) + '</span></div>';
        } else if (state.toggle[g.key]) {
          rows += '<div class="row"><span>' + escapeHtml(g.name) + '</span><span>Ja</span></div>';
        }
      });
      rows += '<div class="row total"><span>Gesamt</span><span>' + money(r.total) + '</span></div>';
      return rows;
    }
    // plain-text version for the hidden contact[body]
    function offerSummaryText() {
      var r = compute();
      var lines = ['ANGEBOTSANFRAGE (Konfigurator)', 'Modell: ' + data.model.title,
        'Paket: ' + (r.pkg ? r.pkg.name + ' (' + money(r.pkg.price) + ')' : '—'), '', 'Auswahl:'];
      data.groups.forEach(function (g) {
        var el = groupEl(g.key);
        if (!el) return;
        if (g.hasOptions) { var val = state.sel[g.key]; if (val) lines.push('• ' + g.name + ': ' + optionLabel(el, val)); }
        else if (state.toggle[g.key]) lines.push('• ' + g.name + ': Ja');
      });
      lines.push('', 'Gesamtpreis: ' + money(r.total), 'Seite: ' + window.location.href);
      return lines.join('\n');
    }

    // fields hidden on success are restored when the popup is reopened
    var MODAL_PARTS = '.mp-modal-field, .mp-modal-summary, .mp-modal-actions, .mp-modal-note';

    function openModal(m) {
      if (!m) return;
      var ok = m.querySelector('[data-mp-modal-success]'); if (ok) ok.hidden = true;
      var err = m.querySelector('[data-mp-modal-error]'); if (err) err.hidden = true;
      m.setAttribute('data-mp-opened-at', String(Date.now())); // spam trap: bots submit instantly
      m.querySelectorAll(MODAL_PARTS).forEach(function (f) { f.style.display = ''; });
      m.classList.add('open');
      document.body.classList.add('mp-modal-open');
      var first = m.querySelector('input, textarea');
      if (first) setTimeout(function () { first.focus(); }, 30);
    }
    function closeModal(m) { if (m) m.classList.remove('open'); document.body.classList.remove('mp-modal-open'); }
    function openOffer() {
      if (!offerModal) return;
      var sum = offerModal.querySelector('[data-mp-modal-summary]'); if (sum) sum.innerHTML = offerSummaryHTML();
      var body = offerModal.querySelector('[data-mp-modal-body]'); if (body) body.value = offerSummaryText();
      openModal(offerModal);
    }

    modals.forEach(function (m) {
      m.querySelectorAll('[data-mp-modal-close]').forEach(function (b) { b.addEventListener('click', function () { closeModal(m); }); });
      m.addEventListener('click', function (e) { if (e.target === m) closeModal(m); }); // backdrop click
      var form = m.querySelector('form');
      if (!form) return;
      form.addEventListener('submit', function (e) {
        e.preventDefault();
        var errEl = m.querySelector('[data-mp-modal-error]');
        function showError(msg) { if (!errEl) { alert(msg); return; } errEl.textContent = msg; errEl.hidden = false; }
        if (errEl) errEl.hidden = true;

        // Spam traps. Required fields are enforced by the browser before this event fires;
        // these two catch the bots that fill every field of every form they find.
        var hp = form.querySelector('[data-mp-hp]');
        if (hp && hp.value.trim()) {
          // silently drop — never tell a bot why
          closeModal(m);
          return;
        }
        var openedAt = Number(m.getAttribute('data-mp-opened-at') || 0);
        if (openedAt && Date.now() - openedAt < 3000) {
          showError('Bitte nimm dir einen Moment für das Formular und sende es erneut ab.');
          return;
        }
        if (hp) hp.disabled = true; // keep the honeypot out of the submitted data

        var submitBtn = form.querySelector('button[type="submit"], .submit');
        if (submitBtn) { submitBtn.disabled = true; submitBtn.classList.add('is-loading'); }
        fetch('/contact', { method: 'POST', body: new FormData(form), headers: { 'X-Requested-With': 'XMLHttpRequest' } })
          .then(function (res) {
            if (!res.ok) throw new Error('contact form ' + res.status);
            var ok = m.querySelector('[data-mp-modal-success]'); if (ok) ok.hidden = false;
            m.querySelectorAll(MODAL_PARTS).forEach(function (f) { f.style.display = 'none'; });
            setTimeout(function () { closeModal(m); }, 2000);
          })
          .catch(function () { showError('Senden fehlgeschlagen. Bitte erneut versuchen.'); })
          .finally(function () {
            if (hp) hp.disabled = false;
            if (submitBtn) { submitBtn.disabled = false; submitBtn.classList.remove('is-loading'); }
          });
      });
    });
    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape') return;
      closePop();
      modals.forEach(function (m) { if (m.classList.contains('open')) closeModal(m); });
    });

    if (offerBtn) offerBtn.addEventListener('click', openOffer);
    if (callBtn) callBtn.addEventListener('click', function () { openModal(callModal); });

    // ---- persistence ----
    function save() {
      if (!saveEnabled) return;
      try { localStorage.setItem(storeKey, JSON.stringify(state)); } catch (e) {}
    }
    function restore() {
      if (!saveEnabled) return;
      try {
        var raw = localStorage.getItem(storeKey);
        if (!raw) return;
        var s = JSON.parse(raw);
        if (s && s.pkgKey) state.pkgKey = s.pkgKey;
        if (s && s.sel) state.sel = s.sel;
        if (s && s.toggle) state.toggle = s.toggle;
        if (s && s.auto) state.auto = s.auto;
      } catch (e) {}
    }

    // pre-select defaults: independent finish groups always have a choice (first
    // real option). Paid groups stay "none" until the customer picks. Dependent
    // groups are handled by normalize(). Saved state wins.
    function applyDefaults() {
      data.groups.forEach(function (g) {
        if (g.productHandle || !g.hasOptions || g.dependsOn) return; // independent finishes only
        if (state.sel[g.key]) return;                                // respect restored selection
        var el = groupEl(g.key);
        var def = el && (el.querySelector('[data-mp-default]:not([data-mp-none])') || el.querySelector('[data-mp-option]:not([data-mp-none])'));
        if (def) state.sel[g.key] = def.getAttribute('data-mp-option');
      });
    }

    // a ?variant= link points at one package — it wins over the saved configuration
    function applyUrlPackage() {
      var vid;
      try { vid = new URLSearchParams(window.location.search).get('variant'); } catch (e) { return; }
      if (!vid) return;
      var match = data.model.packages.filter(function (p) { return String(p.variantId) === String(vid); })[0];
      if (match) state.pkgKey = match.key;
    }

    restore();
    applyUrlPackage();
    applyDefaults();
    render();
    syncGallery(); // restored/linked package may differ from the variant the page rendered with
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }
})();
